import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../config/supabase';
import type { Dress } from '../../config/supabase';

// Query Keys for consistent caching
export const dressesKeys = {
  all: ['dresses'] as const,
  lists: () => [...dressesKeys.all, 'list'] as const,
  list: (page?: number, pageSize?: number, search?: string, brandId?: string, categoryId?: string, color?: string, branchId?: string) =>
    [...dressesKeys.lists(), { page, pageSize, search, brandId, categoryId, color, branchId }] as const,
  allDresses: () => [...dressesKeys.all, 'all'] as const,
  details: () => [...dressesKeys.all, 'detail'] as const,
  detail: (id: string) => [...dressesKeys.details(), id] as const,
};

// Fetch dresses with pagination, search, and filters
export const useDresses = (
  page: number = 1,
  pageSize: number = 10,
  search: string = '',
  brandId?: string,
  categoryId?: string,
  color?: string,
  branchId?: string
) => {
  return useQuery({
    queryKey: dressesKeys.list(page, pageSize, search, brandId, categoryId, color, branchId),
    queryFn: async () => {
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      let query = supabase
        .from('dresses')
        .select(`
          *,
          brands (
            id,
            name
          ),
          categories (
            id,
            name,
            color
          ),
          branches (
            id,
            name
          )
        `, { count: 'exact' });

      // Apply search filter if search term exists
      if (search.trim()) {
        query = query.or(`name.ilike.%${search}%,barcode.ilike.%${search}%,color.ilike.%${search}%`);
      }

      // Apply brand filter
      if (brandId && brandId !== 'all') {
        query = query.eq('brand_id', brandId);
      }

      // Apply category filter
      if (categoryId && categoryId !== 'all') {
        query = query.eq('category_id', categoryId);
      }

      // Apply branch filter
      if (branchId && branchId !== 'all') {
        query = query.eq('branch_id', branchId);
      }

      // Apply color filter
      if (color && color !== 'all') {
        query = query.ilike('color', `%${color}%`);
      }

      query = query
        .order('barcode')
        .range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;
      
      return {
        dresses: data || [],
        totalCount: count || 0,
      };
    },
    // Cache for 2 minutes since dress data doesn't change often
    staleTime: 2 * 60 * 1000,
    // Only enable query when we have valid parameters
    enabled: page > 0 && pageSize > 0,
    // Prevent background refetches during development
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

// Fetch all dresses (for dropdowns)
export const useDressesAll = () => {
  return useQuery({
    queryKey: dressesKeys.allDresses(),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('dresses')
        .select(`
          *,
          brands (
            id,
            name
          ),
          categories (
            id,
            name,
            color
          )
        `)
        .eq('is_available', true)
        .order('name');

      if (error) throw error;
      return data || [];
    },
    // Cache for 5 minutes since dresses don't change often
    staleTime: 5 * 60 * 1000,
    // Prevent background refetches during development
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

// Fetch dresses for select dropdown with search (limit 100 by default)
export const useDressesForSelect = (searchTerm: string = '', limit: number = 100, branchId?: string) => {
  return useQuery({
    queryKey: [...dressesKeys.all, 'select', searchTerm, limit, branchId],
    queryFn: async () => {
      let query = supabase
        .from('dresses')
        .select(`
          id,
          name,
          barcode,
          rental_price,
          color,
          brands (
            id,
            name
          )
        `)
        .eq('is_available', true);

      // Apply branch filter
      if (branchId) {
        query = query.eq('branch_id', branchId);
      }

      // Apply search filter if search term exists
      if (searchTerm.trim()) {
        query = query.or(`name.ilike.%${searchTerm}%,barcode.ilike.%${searchTerm}%,color.ilike.%${searchTerm}%`);
      }

      query = query
        .order('name')
        .limit(limit);

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    },
    // Cache for 2 minutes since we want fresh search results
    staleTime: 2 * 60 * 1000,
    // Don't cache empty searches for too long
    gcTime: searchTerm.trim() ? 5 * 60 * 1000 : 1 * 60 * 1000,
    // Prevent background refetches during development
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

// Fetch unique colors for filter dropdown
export const useDressColors = () => {
  return useQuery({
    queryKey: [...dressesKeys.all, 'colors'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('dresses')
        .select('color')
        .not('color', 'is', null)
        .order('color');

      if (error) throw error;
      
      // Get unique colors
      const uniqueColors = Array.from(new Set(data?.map(d => d.color).filter(Boolean)));
      return uniqueColors.sort();
    },
    // Cache for 10 minutes since colors don't change often
    staleTime: 10 * 60 * 1000,
    // Prevent background refetches during development
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

// Create dress mutation
export const useCreateDress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dressData: Omit<Dress, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('dresses')
        .insert(dressData)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // Invalidate and refetch dress lists
      queryClient.invalidateQueries({ queryKey: dressesKeys.lists() });
    },
  });
};

// Update dress mutation  
export const useUpdateDress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Dress> & { id: string }) => {
      const { data, error } = await supabase
        .from('dresses')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      // Update the specific dress in cache
      queryClient.setQueryData(dressesKeys.detail(data.id), data);
      // Invalidate dress lists to refetch
      queryClient.invalidateQueries({ queryKey: dressesKeys.lists() });
    },
  });
};

// Delete dress mutation
export const useDeleteDress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('dresses')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      // Invalidate dress lists
      queryClient.invalidateQueries({ queryKey: dressesKeys.lists() });
    },
  });
}; 